import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Typography, Tag, Input, Tabs, Pagination, Spin, Empty, Select, Button } from 'antd';
import { DownloadOutlined, ImportOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { skillsApi } from '../services/api';
import BRAND from '../config/brand';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SkillCard = styled(Card)`
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
`;

function Skills() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [skills, setSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });

  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';

  useEffect(() => {
    fetchCategories();
    fetchSkills();
  }, [category, search, sort, pagination.page]);

  const fetchCategories = async () => {
    try {
      const res = await skillsApi.getCategories();
      setCategories(res.data.data || []);
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const params = {
        category: category === 'all' ? '' : category,
        search,
        sort,
        page: pagination.page,
        limit: pagination.limit,
      };
      const res = await skillsApi.getSkills(params);
      
      setSkills(res.data.data || []);
      setPagination(res.data.pagination || pagination);
    } catch (error) {
      console.error('获取技能失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', value);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleCategoryChange = (catCode) => {
    const newParams = new URLSearchParams(searchParams);
    if (catCode && catCode !== 'all') {
      newParams.set('category', catCode);
    } else {
      newParams.delete('category');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSkillClick = (skillId) => {
    navigate(`/skill/${skillId}`);
  };

  const getCategoryIcon = (code) => {
    const cat = categories.find(c => c.code === code);
    return cat ? cat.icon : '📦';
  };

  const getCategoryName = (code) => {
    const cat = categories.find(c => c.code === code);
    return cat ? cat.name : code;
  };

  return (
    <div>
      <PageHeader>
        <Title level={2}>百音技能商店</Title>
        <Paragraph type="secondary">
          {BRAND.slogan} · 发现和领取音乐行业 AI 技能
        </Paragraph>
      </PageHeader>

      <FilterBar>
        <Search
          placeholder="搜索技能名称、描述..."
          value={search}
          onSearch={handleSearch}
          enterButton
          allowClear
          style={{ width: 320 }}
        />
        <Select
          value={sort}
          onChange={handleSortChange}
          style={{ width: 140 }}
          options={[
            { value: 'comprehensive', label: '综合排序' },
            { value: 'hot', label: '热门' },
            { value: 'newest', label: '最新' },
          ]}
        />
        <Button
          type="primary"
          icon={<ImportOutlined />}
          onClick={() => navigate('/skill-import')}
        >
          技能导入
        </Button>
      </FilterBar>

      <Tabs
        activeKey={category}
        onChange={handleCategoryChange}
        style={{ marginBottom: 24 }}
        type="card"
      >
        <TabPane tab="全部" key="all" />
        {categories.map((cat) => (
          <TabPane 
            tab={`${cat.icon} ${cat.name}`} 
            key={cat.code}
          />
        ))}
      </Tabs>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
        </div>
      ) : skills.length === 0 ? (
        <Empty description="暂无技能" style={{ padding: '100px' }} />
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {skills.map((skill) => (
              <Col span={8} key={skill.id}>
                <SkillCard onClick={() => handleSkillClick(skill.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ 
                      width: 48, 
                      height: 48, 
                      background: '#f0f0f0', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24
                    }}>
                      {getCategoryIcon(skill.category)}
                    </div>
                    <div style={{ marginLeft: 12, flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{skill.displayName}</div>
                      <div style={{ fontSize: 12 }}>
                        {skill.type === 1 ? (
                          <Tag color="blue">官方</Tag>
                        ) : (
                          <Tag>用户</Tag>
                        )}
                        <Tag>{getCategoryName(skill.category)}</Tag>
                      </div>
                    </div>
                  </div>
                  <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 16 }}>
                    {skill.description}
                  </Paragraph>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        ⭐ {skill.rating}
                      </Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: 12, color: '#666' }}>
                        <DownloadOutlined /> {skill.downloadCount}
                      </span>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        v{skill.version}
                      </Text>
                    </div>
                  </div>
                </SkillCard>
              </Col>
            ))}
          </Row>
          
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Pagination
              current={pagination.page}
              pageSize={pagination.limit}
              total={pagination.total}
              onChange={(page) => setPagination({ ...pagination, page })}
              showSizeChanger={false}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Skills;
